import React, { useCallback, useState } from 'react';
import { userAppContext } from '../../context/Context';
import ResetApiKey from '../../services/ResetApiKey';
import { useUserApiKey } from '../../hooks/useUserApiKey';
import { EyeIcon, EyeSlashIcon, ClipboardDocumentIcon } from '@heroicons/react/24/solid';
import Alert from '@/global/Alert/Alert';
import SharedButton from '@/global/Button/Button';

interface ProfileApiKeyProps {
  apiKeyClass: string;
}

const ProfileApiKey: React.FC<ProfileApiKeyProps> = ({ apiKeyClass }) => {
  const { user, userApiKey: contextUserApiKey, setUserApiKey: contextSetUserApiKey } = userAppContext();
  const {
    userApiKey,
    userResetApiKey,
    isPassVisible,
    isExternal,
    isStandard,
    apiKeyDisplayStatus,
    togglePassVisibility,
    handleResetApiKeyData,
    setApiKeyRequest,
  } = useUserApiKey({
    user,
    userApiKey: contextUserApiKey,
    setUserApiKey: contextSetUserApiKey,
  });

  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showResetApiKeyForm, setShowResetApiKeyForm] = useState(false);
  const [apiKeyResetSuccess, setApiKeyResetSuccess] = useState(false);

  const toggleResetApiKeyForm = () => {
    setShowResetApiKeyForm(prev => !prev);
  };

  const toggleApiKeyVisibility = () => {
    setIsApiKeyVisible(prev => !prev);
  };

  const handleCopy = useCallback(() => {
    if (userApiKey) {
      navigator.clipboard.writeText(userApiKey);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 10000);
    }
  }, [userApiKey]);

  const resetApiKey = async (event: React.FormEvent<HTMLFormElement>) => {
    await handleResetApiKeyData(event);
    setShowResetApiKeyForm(false);
    setApiKeyRequest({});
    setApiKeyResetSuccess(true);
  };

  const displayValue =
    userApiKey ||
    (apiKeyDisplayStatus === 'standard'
      ? 'API keys are not available on the Standard plan.'
      : apiKeyDisplayStatus === 'external'
        ? 'API key management is handled by your identity provider.'
        : apiKeyDisplayStatus === 'unset'
          ? 'Loading your API key…'
          : apiKeyDisplayStatus);

  return (
    <>
      <div className={`${apiKeyClass} `}>
        <h5>Your API Key</h5>
        <div className="bg-white border rounded-xl p-4 mb-4">
          {isStandard ? (
            <div>
              <p className="text-sm text-gray-700 mb-2">
                REST API access is not included in the Standard plan. Upgrade to Advanced to unlock your
                API key and live requests.
              </p>
              <a href="/plans" className="text-pink font-semibold text-sm underline">
                View plans
              </a>
            </div>
          ) : (
            <>
              <div className="flex">
                <input
                  readOnly
                  type={isApiKeyVisible ? 'text' : 'password'}
                  className="w-full bg-gray-100 border rounded-xl p-2 mr-2"
                  value={displayValue}
                />
                <button
                  type="button"
                  className="bg-gray-100 border rounded-xl px-2 mr-2"
                  onClick={() => toggleApiKeyVisibility()}
                  disabled={!userApiKey}
                >
                  {isApiKeyVisible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
                </button>
                {userApiKey && (
                  <button onClick={handleCopy} className="bg-gray-100 border rounded-xl px-2">
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div>
                <small className="ml-2 text-gray-500">
                  Keep your API Key secret and do not share it with anyone.
                </small>
              </div>
              <div>
                {!isExternal && (
                  <button
                    className="text-pink text-uppercase font-semibold ml-2 mt-4"
                    onClick={toggleResetApiKeyForm}
                  >
                    Reset Your API Key
                  </button>
                )}
              </div>
              {showResetApiKeyForm && (
                <div>
                  <form onSubmit={resetApiKey}>
                    <div className="text-left w-full">
                      <div className="flex justify-between mt-4">
                        <label
                          className="text-xs font-semibold text-gray-600 ml-2 text-uppercase"
                          htmlFor="InputPassword"
                        >
                          Password
                        </label>
                        <button type="button" className="eye-icon" onClick={() => togglePassVisibility()}>
                          {isPassVisible ? (
                            <EyeIcon className="w-4 h-4 mb-1" />
                          ) : (
                            <EyeSlashIcon className="w-4 h-4 mb-1" />
                          )}
                        </button>
                      </div>
                    </div>
                    <input
                      type={`${isPassVisible ? 'text' : 'password'}`}
                      className="form-input"
                      id="password"
                      name="password"
                      placeholder="password"
                      required
                    />
                    <div className="text-center">
                      <SharedButton type="submit" className="w-1/4" variant="gradient">
                        Submit
                      </SharedButton>
                    </div>
                  </form>
                  <span
                    className="text-gray-600 hover:text-blue-950 text-xs mr-2 cursor-pointer flex justify-end mt-2 font-semibold"
                    onClick={() => toggleResetApiKeyForm()}
                  >
                    Cancel
                  </span>
                  <ResetApiKey setApiKeyRequest={setApiKeyRequest} userResetApiKey={userResetApiKey} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {copied && <Alert message="Api Key copied successfully" type="success" />}
      {apiKeyResetSuccess && <Alert message="Api Key reset successfully" type="success" />}
    </>
  );
};

export default ProfileApiKey;
